import React, { MutableRefObject, ReactNode, useImperativeHandle } from 'react';
import { Stack, Button } from '@mui/material';
import { FormProvider, useWatch } from 'react-hook-form';
import { InventorySearchField, InventoryEquipment } from 'modules/inventory';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { EquipmentFormValue } from '../../../types';
import { LoadingFallback } from '../LoadingFollback';
import { EquipmentFormMapper } from '../../../mapper';
import { prepareCreateUpdateEquipmentDTO } from '../../../lib';
import { useEquipmentForm } from '../../../hooks/useEquipmentForm';
import { Attachments, CommonInfo, Pricing, Totals } from './sections';
import { CreateEquipmentVariables, UpdateEquipmentVariables } from '../../../api/equipment/types';

interface Props {
  equipmentId?: string;
  onCancel?: VoidFunction;
  imagesRender?: (selectedEquipmentId?: string) => ReactNode;
  submitRef?: MutableRefObject<{ submit: () => Promise<EquipmentFormValue | null> } | null>;
  onSubmit?: (data: {
    form: EquipmentFormValue;
    createDTO: Omit<CreateEquipmentVariables['input'], 'dynamicsVendorOpportunityId' | 'vendorOpportunityId'>;
    updateDTO: Omit<UpdateEquipmentVariables['input'], 'dynamicsVendorOpportunityId' | 'vendorOpportunityId'>;
  }) => void;
}

export const EquipmentForm = ({ imagesRender, equipmentId, submitRef, onSubmit, onCancel }: Props) => {
  const { form, isLoading } = useEquipmentForm({ id: equipmentId });
  const inventoryOption = useWatch({ control: form.control, name: 'commonInfo.inventoryItem.inventoryOption' });

  const handleSelectInventoryItem = (item: InventoryEquipment) => {
    const mapped = EquipmentFormMapper.fromInventoryItem(item);

    Object.entries(mapped).forEach(([key, value]) => {
      form.setValue(
        key as keyof Pick<EquipmentFormValue, 'year' | 'make' | 'model' | 'commonInfo' | 'calcPricing' | 'attachments'>,
        value,
        { shouldValidate: true }
      );
    });
  };

  useImperativeHandle(submitRef, () => ({
    submit: async () => {
      const isValid = await form.trigger();
      return isValid ? form.getValues() : null;
    },
  }));

  return (
    <RenderWithFallback isLoading={isLoading} fallBack={<LoadingFallback />}>
      <FormProvider {...form}>
        <Stack padding="1rem" direction="row" gap="1rem">
          {!!imagesRender && imagesRender(inventoryOption?.value)}

          <Stack gap="1.5rem" width="100%">
            <InventorySearchField<EquipmentFormValue>
              control={form.control}
              extraAction={handleSelectInventoryItem}
              name="commonInfo.inventoryItem.inventoryOption"
            />

            <CommonInfo />

            <Pricing />

            <Attachments />

            <Totals />

            <RenderWithCondition condition={!!onSubmit}>
              <Stack direction="row" gap="0.5rem" justifyContent="flex-end">
                <RenderWithCondition condition={!!onCancel}>
                  <Button variant="outlined" onClick={onCancel}>
                    Cancel
                  </Button>
                </RenderWithCondition>

                <Button
                  variant="contained"
                  onClick={form.handleSubmit(data => {
                    const dto = prepareCreateUpdateEquipmentDTO(data);
                    onSubmit!({ form: data, createDTO: dto, updateDTO: { ...dto, equipmentId: equipmentId! } });
                  })}
                >
                  Save
                </Button>
              </Stack>
            </RenderWithCondition>
          </Stack>
        </Stack>
      </FormProvider>
    </RenderWithFallback>
  );
};
